/**
 * ACHIEVEMENTS STORE
 *
 * Shape (persisted under the key "achievements"):
 *   unlocked  Record<achievementId, number>  unlock date, epoch ms
 *   featured  string[]                       up to 6 badges shown on PROFILE
 *   counters  { packsOpened, genesisPulled, tutorialCompleted, monoWins }
 *   pending   string[]                       unlocked but not yet toasted
 *
 * The counters exist because no other store owned them: the profile keeps
 * lifetime wins, the collection keeps owned cards, and nothing was counting
 * packs, Genesis pulls, tutorial completion or which factions have taken a
 * mono deck win. They are cumulative and never reset by a match.
 *
 * `pending` is a queue, not a log. `src/achievements/AchievementToasts.tsx`
 * drains it into the toast layer, which is the one place an unlock becomes
 * visible. Draining is idempotent, so a second mount cannot re-announce.
 *
 * Other engineers: call `recordMatchForAchievements` in src/achievements/hooks
 * rather than writing here directly. It builds the evaluator context from every
 * store at once, which is the only way a cross-store condition can be right.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Faction } from '@/engine';
import { adapterStorage } from '@/persist';
import { emptyCounters, type AchievementCounters } from '@/achievements/evaluator';

export const MAX_FEATURED = 6;

export interface AchievementsState {
  unlocked: Record<string, number>;
  featured: string[];
  counters: AchievementCounters;
  pending: string[];
}

export interface AchievementsActions {
  /** Record unlocks. Already-unlocked ids are left alone, keeping their date. */
  unlock(ids: string[], at?: number): string[];
  isUnlocked(id: string): boolean;
  unlockedAt(id: string): number | null;
  /** Add or remove a badge from the PROFILE showcase, capped at MAX_FEATURED. */
  toggleFeatured(id: string): void;
  setFeatured(ids: string[]): void;
  countPacksOpened(count?: number): void;
  countGenesisPulled(count?: number): void;
  recordMonoWin(faction: Faction): void;
  completeTutorial(): void;
  /** Take everything queued for a toast and clear the queue. */
  drainPending(): string[];
  reset(): void;
}

export type AchievementsStore = AchievementsState & AchievementsActions;

export function defaultAchievements(): AchievementsState {
  return { unlocked: {}, featured: [], counters: emptyCounters(), pending: [] };
}

export const useAchievements = create<AchievementsStore>()(
  persist(
    (set, get) => ({
      ...defaultAchievements(),

      unlock(ids, at = Date.now()) {
        const unlocked = { ...get().unlocked };
        const fresh: string[] = [];
        for (const id of ids) {
          if (unlocked[id] !== undefined) continue;
          unlocked[id] = at;
          fresh.push(id);
        }
        if (fresh.length === 0) return [];
        set({ unlocked, pending: [...get().pending, ...fresh] });
        return fresh;
      },

      isUnlocked(id) {
        return get().unlocked[id] !== undefined;
      },

      unlockedAt(id) {
        return get().unlocked[id] ?? null;
      },

      toggleFeatured(id) {
        const featured = get().featured;
        if (featured.includes(id)) {
          set({ featured: featured.filter((f) => f !== id) });
          return;
        }
        if (featured.length >= MAX_FEATURED) return;
        set({ featured: [...featured, id] });
      },

      setFeatured(ids) {
        set({ featured: ids.slice(0, MAX_FEATURED) });
      },

      countPacksOpened(count = 1) {
        const c = get().counters;
        set({ counters: { ...c, packsOpened: c.packsOpened + count } });
      },

      countGenesisPulled(count = 1) {
        const c = get().counters;
        set({ counters: { ...c, genesisPulled: c.genesisPulled + count } });
      },

      recordMonoWin(faction) {
        const c = get().counters;
        if (c.monoWins.includes(faction)) return;
        set({ counters: { ...c, monoWins: [...c.monoWins, faction] } });
      },

      completeTutorial() {
        const c = get().counters;
        if (c.tutorialCompleted) return;
        set({ counters: { ...c, tutorialCompleted: true } });
      },

      drainPending() {
        const pending = get().pending;
        if (pending.length > 0) set({ pending: [] });
        return pending;
      },

      reset() {
        set({ ...defaultAchievements() });
      },
    }),
    {
      name: 'achievements',
      storage: adapterStorage<Pick<AchievementsStore, 'unlocked' | 'featured' | 'counters' | 'pending'>>(),
      partialize: (state) => ({
        unlocked: state.unlocked,
        featured: state.featured,
        counters: state.counters,
        pending: state.pending,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AchievementsState>;
        return { ...current, ...p, counters: { ...emptyCounters(), ...(p.counters ?? {}) } };
      },
    },
  ),
);
